import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

const LoadingScreen = ({ label }: { label: string }) => (
  <div className="min-h-screen bg-lr-dark flex items-center justify-center">
    <div className="text-white text-sm">{label}</div>
  </div>
);

const destinationForRole = (userType: string | null | undefined, billingMode: string | null): string => {
  // LR Direct subscribers (broker tiers) land directly on the LR Direct board.
  if ((billingMode === "lr_direct" || billingMode === "lr_direct_private") &&
      (userType === "broker" || userType === "broker_carrier")) {
    return "/lr-direct";
  }
  switch (userType) {
    case "broker":
    case "broker_carrier":
      return "/broker-dashboard";
    case "carrier":
      return "/carrier-dashboard";
    case "shipper":
      return "/lr-direct/my-opportunities";
    default:
      return "/";
  }
};

/**
 * One-shot landing after Stripe (billing portal or reactivation checkout) returns.
 * Polls subscriptions for up to ~20s waiting for the webhook to provision the row,
 * then invalidates the billing-mode cache and routes the user. Prevents the
 * "paid but still blocked" race that hit ryan@shipcentex.com.
 */
export default function BillingReturnPage() {
  const { user, loading, profile, hasActiveSubscription, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [phase, setPhase] = useState<"checking" | "done">("checking");
  const [billingMode, setBillingMode] = useState<string | null>(null);

  useEffect(() => {
    if (loading || !user) return;
    let cancelled = false;

    const fetchBillingMode = async (): Promise<string | null> => {
      const { data: membership } = await supabase
        .from("company_users")
        .select("company_id")
        .eq("user_id", user.id)
        .eq("is_primary", true)
        .eq("is_active", true)
        .maybeSingle();
      if (!membership?.company_id) return null;
      const { data: sub } = await supabase
        .from("subscriptions")
        .select("billing_mode, status")
        .eq("company_id", membership.company_id)
        .maybeSingle();
      if (!sub) return null;
      if (sub.status !== "active" && sub.status !== "trialing") return null;
      return (sub as any).billing_mode ?? null;
    };

    (async () => {
      try { await refreshProfile?.(); } catch { /* ignore */ }

      // Poll up to ~20s for the webhook to settle (1s intervals).
      const MAX_ATTEMPTS = 20;
      let mode: string | null = null;
      for (let i = 0; i < MAX_ATTEMPTS; i++) {
        if (cancelled) return;
        mode = await fetchBillingMode();
        if (mode) break;
        await new Promise((r) => setTimeout(r, 1000));
      }
      if (cancelled) return;

      try { await refreshProfile?.(); } catch { /* ignore */ }
      // Force-refresh billing-mode cache so route guards / paywalls see the new state.
      await queryClient.invalidateQueries({ queryKey: ["billing-mode", user.id] });
      if (cancelled) return;
      setBillingMode(mode);
      setPhase("done");
    })();
    return () => { cancelled = true; };
  }, [loading, user, refreshProfile, queryClient]);

  if (loading || phase === "checking") {
    const source = searchParams.get("source");
    return <LoadingScreen label={source === "reactivate" ? "Activating your subscription…" : "Finalizing your subscription…"} />;
  }

  if (!user) return <Navigate to="/auth?mode=login" replace />;

  if (!hasActiveSubscription && !billingMode) {
    return <Navigate to="/reactivate" replace />;
  }

  return <Navigate to={destinationForRole(profile?.user_type, billingMode)} replace />;
}
